import { FC, useCallback } from "react";
import {
  capitalize,
  Dialog,
  DialogContent,
  Stack,
  Theme,
  TypographyProps,
} from "@mui/material";
import { useDidMount, useWillUnmount } from "rooks";
import { ReportsDrawerAttachments } from "../../../../reports/components/drawer/components/attachments-section/ReportsDrawerAttachments";
import { SendReportActionItemProps } from "../../SendReportActionItem";
import { getPressEnterHandler } from "@/components/utils/helpers/getPressEnterHandler";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { sendOneTimeReportThunk } from "@/store/reports/thunks/one-time/sendOneTimeReportThunk";
import { sendSingleReportLoadingSelector } from "@/store/reports/selector/sendSingleReportLoadingSelector";
import { resetReportsData } from "@/store/reports/reportsSlice";
import { sendSingleReportButtonDisabledSelector } from "@/store/reports/selector/sendSingleReportButtonDisabledSelector";
import { OneTimeReportType } from "@/components/pages/common/send-report/utils/types/types";
import { DialogTitleClosable } from "@/components/common/dialog-utils/DialogTitleClosable";
import { SendReportDialogActions } from "@/components/pages/common/send-report/components/dialog/SendReportDialogActions";
import { SendReportIncludeAiToggle } from "@/components/pages/common/send-report/components/dialog/SendReportIncludeAiToggle";
import { initiateOneTimeReportThunk } from "@/store/reports/thunks/one-time/initiateOneTimeReportThunk";
import { SendReportEmailSection } from "@/components/pages/common/send-report/components/dialog/SendReportEmailSection";
import { SendReportSlackSection } from "@/components/pages/common/send-report/components/dialog/SendReportSlackSection";

interface SendReportDialogProps extends SendReportActionItemProps {
  onClose(): void;
  type: OneTimeReportType;
}

export const SendReportDialog: FC<SendReportDialogProps> = ({
  type,
  onClose,
  sourceId,
  sourceType,
  sourceName,
}) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(sendSingleReportLoadingSelector);
  const disabled = useAppSelector((state) =>
    sendSingleReportButtonDisabledSelector(state, type),
  );

  const handleSend = useCallback(async () => {
    await dispatch(sendOneTimeReportThunk());
    onClose();
  }, [dispatch, onClose]);

  const handlePressEnter = getPressEnterHandler(
    handleSend,
    disabled ?? loading,
  );

  useDidMount(() => {
    dispatch(
      initiateOneTimeReportThunk({
        sourceId,
        sourceName,
        sourceType,
        reportType: type,
      }),
    );
  });

  useWillUnmount(() => {
    dispatch(resetReportsData());
  });

  return (
    <Dialog
      open={true}
      onClose={onClose}
      onKeyDown={handlePressEnter}
      fullWidth
      maxWidth="md"
      sx={dialogStyles}
      PaperProps={paperProps}
    >
      <DialogTitleClosable
        onClose={onClose}
        title={`Send ${capitalize(type)}`}
        TitleTypographyProps={titleTypographyProps}
      />

      <DialogContent dividers>
        <Stack spacing={3}>
          {type === "email" ? (
            <SendReportEmailSection />
          ) : (
            <SendReportSlackSection />
          )}

          <Stack spacing={2}>
            {sourceType !== "dashboard" && <SendReportIncludeAiToggle />}
            <ReportsDrawerAttachments />
          </Stack>
        </Stack>
      </DialogContent>

      <SendReportDialogActions
        type={type}
        onClose={onClose}
        onSend={handleSend}
      />
    </Dialog>
  );
};

const paperProps = { sx: { borderRadius: 2, overflowX: "hidden" } };

const titleTypographyProps: TypographyProps = {
  variant: "subtitle1",
};

const dialogStyles = {
  zIndex: (theme: Theme) => theme.zIndex.drawer,
};
